import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import styled from "@emotion/styled"
import CTAFormField from "components/CTAForm/CTAFormField"
import Button from "components/Buttons/Button"
import CTAFormFormFieldName from "components/CTAForm/CTAFormFormFieldName"
import { Link } from "gatsby"
import FormFinalImage from "components/Icons/FormFinalImage"
import HubspotForm from "../HubspotForm"

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: 0 1rem;
  }
`

const ModalWrapper = styled.div`
  background: white;
  max-width: 480px;
  margin: 10vh auto;
  padding: 2rem;
  border-radius: 12px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin: 5vh auto;
    padding: 1.5rem;
  }
`

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  button {
    width: 100%;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 0.85rem;
  cursor: pointer;
`

const Heading = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`

const FinalHeading = styled.h2`
  font-size: 1rem;
  line-height: 1.25rem;
  font-weight: 400;
  margin-bottom: 1.5rem;
  text-align: center;
  margin: 0;
`

const Required = styled.span`
  color: red !important;
`

const Policy = styled.p`
  color: ${({ theme }) => theme.palette.common.black};

  ${({ theme }) => theme.breakpoints.down("2xl")} {
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 20px;
  }

  ${({ theme }) => theme.breakpoints.up("2xl")} {
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 20px;
  }

  a {
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`

const Form = styled(HubspotForm)`
  display: grid;
  gap: 20px;
`

const FormFinalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 460px;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down("md")} {
    border-radius: 10px;
    min-height: 440px;
  }
`

const ModalForm = ({ onClose, formSubmitLink, formName, formFields = [], formTexts = {} }) => {
  const initialValues = formFields.reduce(
    (acc, field) => {
      acc[field.field] = ""
      return acc
    },
    { lastname: "" },
  )

  const fieldMapping = {
    ...formFields.reduce((acc, field) => {
      if (field.field === "name") {
        acc[field.field] = "firstname"
      } else {
        acc[field.field] = field.field
      }
      return acc
    }, {}),
    lastname: "lastname",
  }

  const [formValues, setFormValues] = useState(initialValues)
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState("")

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <Overlay>
      <ModalWrapper>
        <CloseButton onClick={onClose}>
          {success ? formTexts.closeSuccess : formTexts.close}
        </CloseButton>

        {!success ? (
          <>
            <Heading>{formTexts.title}</Heading>
            {formTexts.subTitle && <p>{formTexts.subTitle}</p>}

            <Form
              formName={formName}
              endpoint={formSubmitLink}
              formValues={formValues}
              setSending={setSending}
              fieldMapping={fieldMapping}
              postSubmit={(isSuccess) => {
                if (isSuccess) {
                  setSuccess(true)
                  setSubmittedEmail(formValues.email)
                }
              }}
            >
              {formFields.map(({ field, label, placeholder, type = "text", required }) => (
                <div key={field}>
                  <CTAFormFormFieldName>
                    {label}
                    {required && <Required> *</Required>}
                  </CTAFormFormFieldName>
                  <CTAFormField
                    type={type}
                    name={field}
                    placeholder={placeholder}
                    required={required}
                    value={formValues[field]}
                    onChange={handleChange}
                  />
                </div>
              ))}
              <Policy>
                By sending this form I confirm that I have read and accept the&nbsp;
                <a rel="noopener noreferrer" target="_blank" href="/privacy-policy/">
                  Privacy Policy
                </a>
              </Policy>
              <ButtonContainer>
                <Button variant="contained" type="submit" disabled={sending}>
                  {sending ? "Sending..." : formTexts.submit}
                </Button>
              </ButtonContainer>
            </Form>
          </>
        ) : (
          <>
            <FormFinalWrapper>
              <FormFinalImage />
              <FinalHeading>
                We are always happy to share our expertise with you! The link to the content will be
                sent in a few minutes to your email, {submittedEmail}. If you have any questions,
                don&apos;t hesitate to contact us.
              </FinalHeading>
            </FormFinalWrapper>
            <ButtonContainer>
              <Button variant="contained" onClick={onClose}>
                {formTexts.closeSuccess}
              </Button>
            </ButtonContainer>
          </>
        )}
      </ModalWrapper>
    </Overlay>
  )
}

ModalForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  formSubmitLink: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  formFields: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      type: PropTypes.string,
      required: PropTypes.bool,
    }),
  ).isRequired,
  formTexts: PropTypes.shape({
    close: PropTypes.string.isRequired,
    closeSuccess: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    successMessage: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
}

export default ModalForm
